if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');

        // Always listen to slide changes (even if thumbnails are hidden on mobile)
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 200));
          this.elements.viewer.addEventListener('slideChanged', this.preloadAdjacentMedia.bind(this));
          // Preload as early as possible (helps prevent a "white flash" when swiping on real mobile devices)
          window.setTimeout(() => this.preloadAdjacentMedia(), 0);
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        // On mobile, force eager loading for gallery images so swipe transitions don't reveal empty slides.
        if (this.mobileMql.matches) this.disableLazyOnMobile();
      }

      disableLazyOnMobile() {
        const imgs = this.querySelectorAll('.product__media img');
        imgs.forEach((img, idx) => {
          // Override lazy loading on mobile for the product gallery
          img.loading = 'eager';
          img.setAttribute('loading', 'eager');
          img.decoding = 'async';
          img.setAttribute('decoding', 'async');
          // Give the first couple images higher priority
          if (idx < 2) {
            try {
              img.fetchPriority = 'high';
            } catch (e) {}
            img.setAttribute('fetchpriority', 'high');
          }
          this.preloadImageTag(img);
        });
      }

      preloadImageTag(img) {
        // Avoid duplicate work
        if (!img || img.dataset.preloaded === 'true') return;
        img.dataset.preloaded = 'true';
        // If the browser hasn't picked a source yet, still try to warm the cache via src/srcset.
        const src = img.getAttribute('src');
        const srcset = img.getAttribute('srcset');
        const sizes = img.getAttribute('sizes');

        // Warm cache using responsive attributes when available
        if (srcset) {
          const pre = new Image();
          pre.decoding = 'async';
          pre.srcset = srcset;
          if (sizes) pre.sizes = sizes;
          if (src) pre.src = src;
          return;
        }

        if (src) {
          const pre = new Image();
          pre.decoding = 'async';
          pre.src = src;
        }
      }

      preloadAdjacentMedia(event) {
        if (!this.elements.viewer) return;
        const slides = this.elements.viewer.sliderItemsToShow?.length
          ? this.elements.viewer.sliderItemsToShow
          : Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
        if (!slides.length) return;

        const currentEl = event?.detail?.currentElement || this.elements.viewer.querySelector('.is-active') || slides[0];
        const currentIndex = Math.max(0, slides.indexOf(currentEl));
        const indicesToPreload = [currentIndex - 1, currentIndex, currentIndex + 1].filter(
          (i) => i >= 0 && i < slides.length
        );

        indicesToPreload.forEach((i) => {
          slides[i].querySelectorAll('img').forEach((img) => {
            img.loading = 'eager';
            img.setAttribute('loading', 'eager');
            img.decoding = 'async';
            img.setAttribute('decoding', 'async');
            this.preloadImageTag(img);
          });
        });
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
